import React, { useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { isIOS, isAndroid } from "@/utils/platformUtils";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { openFilePicker } from "@/lib/native/openFilePicker";
import type { VehicleTestImages } from "@/types/vehicleTest";

interface ImageUploadButtonProps {
  onSelect: () => void;
  onFileSelect?: (files: File[]) => Promise<void>;
  onCameraSelect?: () => void;
  label: React.ReactNode;
  isThumbnail?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  section?: keyof VehicleTestImages;
  tireType?: "primary" | "secondary";
  chooseGalleryLabel?: string;
  chooseCameraLabel?: string;
  chooseSourcePlaceholder?: string;
}

export const ImageUploadButton: React.FC<ImageUploadButtonProps> = ({
  onSelect,
  onFileSelect,
  onCameraSelect,
  label,
  isThumbnail = false,
  disabled = false,
  isLoading = false,
  section,
  tireType,
  chooseGalleryLabel = "Choose from gallery",
  chooseCameraLabel = "Take photo",
  chooseSourcePlaceholder = "Choose…",
}) => {
  const ios = isIOS();
  const { isNative } = useNativePlatform();
  /** Touch phones in landscape often exceed 768px width — still show gallery/camera picker. */
  const android = isAndroid();
  const useCameraSession = Boolean(onCameraSelect);
  const showSourcePicker = useCameraSession && (ios || android || isNative);
  const [showSourceSelect, setShowSourceSelect] = useState(false);

  const pickGalleryFiles = async () => {
    const files = await openFilePicker({
      accept: "image/*",
      multiple: !isThumbnail,
    });
    if (files.length > 0 && onFileSelect) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      await onFileSelect(files);
    }
  };

  const handleSourceSelect = (value: string) => {
    setTimeout(() => {
      if (value === "gallery") {
        if ((ios || isNative) && useCameraSession) {
          void pickGalleryFiles();
        } else {
          onSelect();
        }
      } else if (value === "camera") {
        if (onCameraSelect) {
          onCameraSelect();
        } else {
          onSelect();
        }
      }
      setShowSourceSelect(false);
    }, 100);
  };

  const sourceSelectTrigger = (
    <SelectTrigger className="native-image-source-select w-full h-full border-0 shadow-none focus:ring-0 flex items-center justify-center gap-1.5 px-2">
      <span className="text-xs text-muted-foreground text-center truncate">
        {chooseSourcePlaceholder}
      </span>
    </SelectTrigger>
  );

  const uploadAreaClassName = `w-full h-24 border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors ${
    isThumbnail ? "border-blue-500 border-4" : "border-gray-300"
  } ${disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""}`;

  const uploadAreaContent = isLoading ? (
    <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
  ) : (
    <>
      <Upload className="h-6 w-6 text-gray-400" />
      <span className="text-xs text-gray-500 mt-1 text-center whitespace-normal px-1">
        {label}
      </span>
    </>
  );

  if (showSourcePicker) {
    return (
      <div className="relative w-full h-24">
        {showSourceSelect ? (
          <div className="w-full h-24 border-2 border-dashed rounded flex flex-col items-center justify-center border-gray-300">
            <Select
              onValueChange={handleSourceSelect}
              onOpenChange={setShowSourceSelect}
            >
              {sourceSelectTrigger}
              <SelectContent>
                <SelectItem value="gallery">{chooseGalleryLabel}</SelectItem>
                <SelectItem value="camera">{chooseCameraLabel}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div
            className={uploadAreaClassName}
            onClick={
              disabled || isLoading
                ? undefined
                : () => setShowSourceSelect(true)
            }
          >
            {uploadAreaContent}
          </div>
        )}
      </div>
    );
  }

  if (ios || isNative) {
    return (
      <div
        className={uploadAreaClassName}
        onClick={
          disabled || isLoading
            ? undefined
            : () => {
                void pickGalleryFiles();
              }
        }
        role="button"
        tabIndex={disabled || isLoading ? -1 : 0}
        onKeyDown={
          disabled || isLoading
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void pickGalleryFiles();
                }
              }
        }
      >
        {uploadAreaContent}
      </div>
    );
  }

  return (
    <div
      className={uploadAreaClassName}
      onClick={disabled || isLoading ? undefined : onSelect}
    >
      {uploadAreaContent}
    </div>
  );
};
